import { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle as CheckCircle2, LinkedinLogo as Linkedin, Envelope as Mail } from "@phosphor-icons/react";
import { openBetaModal } from '../lib/beta';
import { RealisticPhone } from '../components/DeviceFrames';

const API = process.env.REACT_APP_BACKEND_URL;

const INK = '#2A1E5C';
const INK2 = '#5D5A6B';
const GOLD = '#F5CB5C';
const CREAM = '#FDFBF7';
const PAPER = '#F5F0E8';

/* Reusable email capture. variant: 'dark' (on ink) | 'light' (on cream/paper) */
function CaptureForm({ cta, successTitle, successBody, variant = 'dark', testid }) {
  const [email, setEmail] = useState('');
  const [wantsBeta, setWantsBeta] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const dark = variant === 'dark';

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes('@') || !clean.includes('.')) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/prelaunch/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean, beta: wantsBeta }),
      });
      if (!res.ok) { setError('Something went wrong. Please try again.'); return; }
      setResult(await res.json().catch(() => null));
      setDone(true);
    } catch {
      setError('Network hiccup. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl p-6 sm:p-7"
        style={{
          background: dark ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
          border: `1px solid ${dark ? 'rgba(245,203,92,0.35)' : PAPER}`,
        }}
        data-testid={`${testid}-success`}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: dark ? GOLD : '#B8860B' }} />
          <div>
            <p className="text-base font-bold mb-1" style={{ color: dark ? '#FFFFFF' : INK, fontFamily: "'Fraunces', Georgia, serif" }}>
              {successTitle}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: dark ? 'rgba(255,255,255,0.75)' : INK2 }}>
              {result?.beta
                ? "You're on the launch list and in the Founding Racers Beta Circle. Your beta welcome email is on its way, and your beta code is inside it."
                : result?.beta_full
                  ? `You're on the launch list. ${result.beta_message || 'The beta is full for now,'} we will tell you the moment a seat opens.`
                  : successBody}
            </p>
            {result?.beta && result?.beta_code && (
              <p
                className="text-sm font-bold mt-3 inline-block px-3 py-1.5 rounded-lg tracking-wide"
                style={{ background: dark ? 'rgba(245,203,92,0.16)' : 'rgba(42,30,92,0.06)', color: dark ? GOLD : INK }}
                data-testid={`${testid}-beta-code`}
              >
                Beta code: {result.beta_code}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${testid}-form`}>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          placeholder="you@example.com"
          aria-label="Email address"
          className="flex-1 px-5 py-4 rounded-full text-base focus:outline-none focus:ring-2 transition-shadow"
          style={{ background: CREAM, border: `1px solid ${dark ? 'rgba(245,203,92,0.35)' : PAPER}`, color: INK }}
          data-testid={`${testid}-input`}
        />
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-bold text-base whitespace-nowrap transition-transform hover:scale-[1.02] disabled:opacity-60"
          style={{ background: GOLD, color: INK }}
          data-testid={`${testid}-submit`}
        >
          {busy ? 'Joining…' : (<>{cta} <ArrowRight className="w-4 h-4" /></>)}
        </button>
      </div>
      <label
        className="flex items-start gap-3 mt-4 -mx-2 px-2 py-2.5 rounded-xl cursor-pointer select-none transition-colors hover:bg-white/[0.04]"
        data-testid={`${testid}-beta-label`}
      >
        <input
          type="checkbox"
          checked={wantsBeta}
          onChange={(e) => setWantsBeta(e.target.checked)}
          className="mt-0.5 w-6 h-6 flex-shrink-0 cursor-pointer accent-[#F5CB5C]"
          data-testid={`${testid}-beta-checkbox`}
        />
        <span className="text-sm leading-relaxed" style={{ color: dark ? 'rgba(255,255,255,0.78)' : INK2 }}>
          <b style={{ color: dark ? '#FFFFFF' : INK }}>I want to be a beta tester.</b> Early access before
          launch, and a beta code for 50% off your full season pass once your free 4-relay sprint ends.
        </span>
      </label>
      {error && (
        <p className="text-sm mt-3" style={{ color: dark ? '#FCA5A5' : '#B01A2B' }} data-testid={`${testid}-error`}>{error}</p>
      )}
    </form>
  );
}

export default function PreLaunchPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Payday Relay, almost here';
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    const desc = 'Payday Relay is a new way for self-employed people to put structure around earning, working, and finishing. Join the launch list.';
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:title"]', 'content', 'Payday Relay is almost here.');
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[property="og:image"]', 'content', `${window.location.origin}/images/prelaunch-og.jpg?v=1`);
    setMeta('meta[name="twitter:image"]', 'content', `${window.location.origin}/images/prelaunch-og.jpg?v=1`);
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: INK }} data-testid="prelaunch-page">
      <style>{`
        @keyframes prPrelaunchFloat {
          0%, 100% { transform: translateY(-10px) rotate(4deg); }
          50% { transform: translateY(10px) rotate(2.5deg); }
        }
        .pr-prelaunch-float { animation: prPrelaunchFloat 7s ease-in-out infinite; }
        @keyframes prBatonFloat {
          0%, 100% { transform: translate3d(0, -6px, 0) rotate(-14deg); }
          50% { transform: translate3d(-6px, 8px, 0) rotate(-7deg); }
        }
        .pr-baton-float { animation: prBatonFloat 5.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pr-prelaunch-float { animation: none; transform: rotate(3.5deg); }
          .pr-baton-float { animation: none; transform: rotate(-12deg); }
        }
      `}</style>
      {/* Soft ambient glow */}
      <div aria-hidden="true" className="pointer-events-none" style={{ position: 'fixed', inset: 0, overflow: 'hidden', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-12%', left: '-8%', width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,203,92,0.14), transparent 70%)' }} />
        <div style={{ position: 'absolute', bottom: '-14%', right: '-6%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,203,92,0.08), transparent 70%)' }} />
      </div>

      {/* ============ HEADER ============ */}
      <header className="relative z-10 px-6 sm:px-10 pt-9 sm:pt-14 pb-3 sm:pb-4">
        <div className="lg:ml-[max(0px,calc((100vw-80rem)/2))] flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:gap-3">
          <img src="/images/paydayrelay-all-white.png" alt="Payday Relay" className="h-[50px] sm:h-[60px] w-auto" data-testid="prelaunch-logo" />
          <button
            onClick={openBetaModal}
            className="px-2.5 py-[3px] rounded-full text-[9px] sm:text-[11px] font-bold tracking-widest leading-none transition-transform hover:scale-105 shadow-sm -mt-1 sm:mt-0"
            style={{ background: GOLD, color: INK }}
            data-testid="beta-open-pill"
          >
            BETA
          </button>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <main className="relative z-10 flex-1 flex items-center px-6 sm:px-10 pt-4 sm:pt-6 pb-16 sm:pb-24">
        <div className="w-full max-w-5xl lg:max-w-[62rem] mx-auto lg:mx-0 lg:ml-[max(0px,calc((100vw-80rem)/2))] flex items-center gap-10 xl:gap-14">
        <div className="flex-1 min-w-0">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF', fontWeight: 500 }} data-testid="prelaunch-headline">
            Payday Relay is <span style={{ color: GOLD }}>almost here.</span>
          </h1>

          <p className="text-lg sm:text-xl leading-relaxed mb-7 sm:mb-8 max-w-xl" style={{ color: 'rgba(255,255,255,0.82)' }} data-testid="prelaunch-subline">
            A new way for self-employed people to put structure around earning, working, and finishing.
          </p>

          {/* ---- One signup. The checkbox is what makes someone a beta racer ---- */}
          <div className="max-w-xl" data-testid="prelaunch-signup-section">
            <h2 className="text-2xl sm:text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF', fontWeight: 500 }}>
              Be the first to hit the track.
            </h2>
            <p className="text-sm sm:text-base mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              We will tell you the moment Payday Relay launches, and if you want in early,
              tick the box to become a beta tester.
            </p>
            <CaptureForm
              cta="Keep me posted"
              successTitle="You're on the list."
              successBody="We'll ping you the moment Payday Relay launches. Check your inbox for a quick note 🏁"
              variant="dark"
              testid="prelaunch-signup"
            />
          </div>
        </div>

        {/* App sneak peek. Its own column, so it never crowds the copy.
            The baton floats on its own timing so the two never move as one lump. */}
        <div className="hidden xl:block shrink-0 w-[232px] relative" aria-hidden="true" data-testid="prelaunch-phone">
          <img
            src="/images/brand-icon.png"
            alt=""
            className="pr-baton-float absolute -top-[72px] -right-[58px] w-[104px] h-auto z-10 drop-shadow-[0_12px_28px_rgba(0,0,0,0.5)]"
            data-testid="prelaunch-phone-baton"
          />
          <div className="pr-prelaunch-float">
            <RealisticPhone src="/images/home-mockup-app-v3.png" alt="Payday Relay app preview" camera="punch" radius={2.4} />
          </div>
        </div>
        </div>
      </main>

      {/* ============ PROBLEM / PHILOSOPHY ============ */}
      <section className="relative z-10 px-6 sm:px-10 py-16 sm:py-24" style={{ background: CREAM }} data-testid="prelaunch-philosophy">
        <div className="max-w-3xl mx-auto lg:mx-0 lg:ml-[max(0px,calc((100vw-80rem)/2))]">
          <div className="grid gap-14 sm:gap-16">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-4" style={{ color: '#B8860B' }}>The problem</p>
              <h2 className="text-2xl sm:text-3xl leading-snug mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
                When no one else defines the workday, it's hard to know when you've reached the finish line.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed" style={{ color: INK2 }}>
                Self-employed work rarely arrives with a schedule. Income shows up in pieces,
                and there's no manager, clock, or paycheck rhythm to keep work contained and
                prevent work and life hours from blurring together. Without those edges, it
                becomes hard to know what today needs from you, or when you can stop with
                confidence.
              </p>
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-4" style={{ color: '#B8860B' }}>The philosophy</p>
              <h2 className="text-2xl sm:text-3xl leading-snug mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
                Structure around money, time, rest, and agency.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed" style={{ color: INK2 }}>
                Payday Relay gives independent earning a structure. It turns your money goals
                into a weekly rhythm, gives each day a
                clear target, and helps you pass progress forward in a weekly relay race with
                quarterly tax days. You start where you are, run one week at a time, and build
                a rhythm that supports your work, your rest, and the life you're working toward.
              </p>
            </div>
          </div>

          <div className="mt-16 pt-8" style={{ borderTop: `1px solid ${PAPER}` }}>
            <p className="text-base font-bold mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              Shari Dove Shepard, Founder
            </p>
            <p className="text-sm mb-5" style={{ color: INK2 }}>
              Follow the journey or say hello.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://www.linkedin.com/in/sharidoveshepard/"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-transform hover:scale-105"
                style={{ background: INK, color: '#FFFFFF' }}
                data-testid="prelaunch-linkedin"
              >
                <Linkedin className="w-4 h-4" />
                <span className="text-sm font-semibold">LinkedIn</span>
              </a>
              <a
                href="https://www.indiehackers.com/sharidove"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-transform hover:scale-105"
                style={{ background: INK, color: '#FFFFFF' }}
                data-testid="prelaunch-indiehackers"
              >
                <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-black" style={{ background: '#FFFFFF', color: INK }}>IH</span>
                <span className="text-sm font-semibold">Indie Hackers</span>
              </a>
              <button
                type="button"
                onClick={() => {
                  const u = ['sharidove', 'paydayrelay.com'];
                  window.location.href = 'mailto:' + u[0] + '@' + u[1];
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-transform hover:scale-105"
                style={{ background: 'transparent', color: INK, border: `1px solid ${INK}` }}
                data-testid="prelaunch-email"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm font-semibold">Email</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative z-10 px-6 sm:px-10 py-8 text-center" style={{ background: INK }} data-testid="prelaunch-footer">
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-3 text-xs font-semibold">
          <a href="/privacy" className="hover:opacity-80" style={{ color: 'rgba(255,255,255,0.8)' }} data-testid="prelaunch-privacy-link">Privacy Policy</a>
          <a href="/terms" className="hover:opacity-80" style={{ color: 'rgba(255,255,255,0.8)' }} data-testid="prelaunch-terms-link">Terms of Service</a>
          <a href="/privacy#google-data" className="hover:opacity-80" style={{ color: 'rgba(255,255,255,0.8)' }} data-testid="prelaunch-google-data-link">How we use Google data</a>
        </div>
        <p className="text-xs leading-relaxed max-w-2xl mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.45)' }} data-testid="prelaunch-limited-use">
          Payday Relay&rsquo;s use and transfer of information received from Google APIs adheres to the{' '}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Google API Services User Data Policy
          </a>, including the Limited Use requirements.
        </p>
        <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Payday Relay is a trademark of Earthwise Digital LLC. Trademark application pending.
        </p>
      </footer>
    </div>
  );
}
